'use client'

import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { darken, getRGBA, lighten } from 'oddle-shop-monorepo.design-system'
import { RootState } from '@/redux/store'

interface TailwindThemeProviderProps {
  children: React.ReactNode
  themeData?: Oms.V4ApiResponse<Customisation.Theme>
  pageData?: Oms.V4ApiResponse<Customisation.SectionSettingData>
  defaultPageData?: Oms.V4ApiResponse<Customisation.SectionSettingData>
}

export function TailwindThemeProvider({
  children,
  themeData,
  pageData,
  defaultPageData,
}: TailwindThemeProviderProps) {
  const customisationState = useSelector(
    (state: RootState) => state['@customisation']
  )
  const styleElementRef = useRef<HTMLStyleElement | null>(null)

  const generateColorVariants = (color: string | undefined, prefix: string) => {
    if (!color) return {}
    try {
      return {
        [`--${prefix}-darker`]: darken(color, 15),
        [`--${prefix}-darkest`]: darken(color, 25),
        [`--${prefix}-lighter`]: lighten(color, 15),
        [`--${prefix}-lightest`]: lighten(color, 25),
      }
    } catch (error) {
      console.warn(`Failed to generate color variants for ${prefix}:`, error)
      return {}
    }
  }

  const generateThemeVariables = (
    themeSettings: Customisation.ThemeSettingData['settings']
  ) => {
    if (!themeSettings) return {} as Record<string, string>

    const buttonTextColor = themeSettings.buttonTextColor
    const buttonColor = themeSettings.buttonColor

    return {
      '--tw-ring-color': 'transparent',
      '--tw-ring-shadow': 'transparent',
      '--tw-text-opacity': '1',
      '--tw-border-opacity': '1',
      '--color-section-headings-font': themeSettings.colorSectionHeadingsFont,
      '--color-headings-font': themeSettings.colorHeadingsFont,
      '--color-body-font': themeSettings.colorBodyFont,
      '--color-lines-and-borders': themeSettings.colorLinesAndBorders,
      '--color-container-color': themeSettings.colorContainerColor,
      '--color-background-color': themeSettings.colorBackgroundColor,
      '--typography-headings': themeSettings.typographyHeadings,
      '--typography-body-text': themeSettings.typographyBodyText,
      '--button-style': themeSettings.buttonStyle,
      '--button-shape': themeSettings.buttonShape,
      '--button-text-color': buttonTextColor,
      '--button-color':
        (buttonColor ? getRGBA(buttonColor) : null) || '0, 0, 0',
      ...generateColorVariants(buttonTextColor, 'button-text-color'),
      ...generateColorVariants(buttonColor, 'button-color'),
      ...generateColorVariants(
        themeSettings.colorContainerColor,
        'color-container-color'
      ),
    }
  }

  const generateHeaderVariables = (
    headerSettings: any
  ): Record<string, string> => {
    if (!headerSettings) return {} as Record<string, string>

    const navLinkColor = headerSettings.navigationLinkFontColor
    const backgroundColor = headerSettings.headerBackgroundColor

    return {
      '--header-nav-link-color': navLinkColor,
      '--header-background-color': backgroundColor,
      ...generateColorVariants(navLinkColor, 'header-nav-link-color'),
      ...generateColorVariants(backgroundColor, 'header-background-color'),
    }
  }

  const getCSSVariables = () => {
    // Default fallback values
    const fallbackVariables = {
      '--button-color': '0, 0, 0',
      '--color-container-color': '#ffffff',
      '--color-background-color': '#ffffff',
      '--color-body-font': '#000000',
      '--color-headings-font': '#000000',
      '--color-section-headings-font': '#000000',
      '--color-lines-and-borders': '#e5e5e5',
      '--header-nav-link-color': '#000000',
      '--header-background-color': '#ffffff',
      '--tw-ring-color': 'transparent',
      '--tw-ring-shadow': 'transparent',
      '--tw-text-opacity': '1',
      '--tw-border-opacity': '1',
    }

    // Get theme settings from different sources based on preview mode
    let themeSettings = null
    let headerSettings = null

    if (customisationState.data.previewMode) {
      // In preview mode, use data from customisation state (updated by updateCustomisationSetting)
      themeSettings = customisationState.data.themeSettingData?.settings
      headerSettings =
        customisationState.data.sectionSettingData?.sections?.header?.settings
    } else {
      // In normal mode, use fetched theme data
      themeSettings = themeData?.data?.themeData?.themeSettingData?.settings

      // For header settings, prioritize pageData over defaultPageData
      if (pageData?.data?.sections?.header?.settings) {
        headerSettings = pageData.data.sections.header.settings
      } else if (defaultPageData?.data?.sections?.header?.settings) {
        headerSettings = defaultPageData.data.sections.header.settings
      }
    }

    // Generate theme variables
    const themeVariables = themeSettings
      ? generateThemeVariables(themeSettings)
      : ({} as Record<string, string>)
    const headerVariables = headerSettings
      ? generateHeaderVariables(headerSettings)
      : ({} as Record<string, string>)

    // Only use fallbacks for variables that aren't provided by theme/header data
    const finalVariables = { ...fallbackVariables } as Record<string, string>

    // Override with actual theme variables
    Object.keys(themeVariables).forEach((key) => {
      if (themeVariables[key] !== undefined && themeVariables[key] !== null) {
        finalVariables[key] = themeVariables[key]
      }
    })

    // Override with actual header variables
    Object.keys(headerVariables).forEach((key) => {
      if (headerVariables[key] !== undefined && headerVariables[key] !== null) {
        finalVariables[key] = headerVariables[key]
      }
    })

    return finalVariables
  }

  const updateCSSVariables = () => {
    // Only run on client side
    if (typeof window === 'undefined') return

    const variables = getCSSVariables()

    // Create or update style element
    if (!styleElementRef.current) {
      styleElementRef.current = document.createElement('style')
      styleElementRef.current.id = 'tailwind-theme-variables'
      document.head.appendChild(styleElementRef.current)
    }

    // Generate CSS
    const cssRules = Object.entries(variables)
      .filter(([_, value]) => value !== null && value !== undefined)
      .map(([key, value]) => `${key}: ${value};`)
      .join('\n    ')

    styleElementRef.current.textContent = `
      :root {
        ${cssRules}
      }
    `
  }

  // Update CSS variables when any theme-related data changes
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return

    updateCSSVariables()
  }, [
    themeData,
    pageData,
    defaultPageData,
    customisationState.data.previewMode,
    customisationState.data.themeSettingData,
    customisationState.data.sectionSettingData,
  ])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (
        typeof window !== 'undefined' &&
        styleElementRef.current &&
        styleElementRef.current.parentNode
      ) {
        try {
          styleElementRef.current.parentNode.removeChild(
            styleElementRef.current
          )
          styleElementRef.current = null
        } catch (error) {
          console.warn('Failed to remove style element:', error)
        }
      }
    }
  }, [])

  return <>{children}</>
}
